import {
  ChangeDetectionStrategy, Component, ElementRef, OnDestroy, afterNextRender, booleanAttribute,
  effect, inject, input, signal, viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AppPageBase, ColorService, EnumRouteName, Recueil } from '@lyricstream/shared';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Grille de cartes de recueils : image de couverture en fond (cover) et sigle coloré
 * avec la couleur du recueil. Réutilisable (Découvrir, Home, page Recueils).
 * Avec `carousel`, les cartes défilent horizontalement (boutons précédent/suivant).
 */
@Component({
  selector: 'ls-recueil-grid',
  standalone: true,
  imports: [RouterLink, TranslateModule],
  templateUrl: './recueil-grid.component.html',
  styleUrl: './recueil-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecueilGridComponent extends AppPageBase implements OnDestroy {
  private readonly colorService = inject(ColorService);

  /** Recueils à afficher. */
  readonly recueils = input.required<Recueil[]>();
  /** Préfixe d'URL des liens (recueils ou groupe-ce), voir `recueilRoute` dans app.routes. */
  readonly basePath = input<string>(EnumRouteName.Recueils);
  /** Affichage en carrousel horizontal plutôt qu'en grille. */
  readonly carousel = input(false, { transform: booleanAttribute });
  readonly showSigle = input(true, { transform: booleanAttribute });

  private readonly trackEl = viewChild<ElementRef<HTMLElement>>('track');
  protected readonly canPrev = signal(false);
  protected readonly canNext = signal(true);
  private cleanup?: () => void;

  constructor() {
    super();
    afterNextRender(() => this.bindScroll());
    // Le conteneur apparaît/disparaît avec `carousel` : on (re)branche le suivi du scroll.
    effect(() => {
      this.trackEl();
      this.recueils();
      queueMicrotask(() => this.bindScroll());
    });
  }

  ngOnDestroy(): void {
    this.cleanup?.();
  }

  protected scroll(dir: 1 | -1): void {
    this.trackEl()?.nativeElement.scrollBy({ left: dir * 320, behavior: 'smooth' });
  }

  private bindScroll(): void {
    const el = this.trackEl()?.nativeElement;
    if (!el || !this.carousel()) return;
    this.cleanup?.();
    const update = () => {
      this.canPrev.set(el.scrollLeft > 4);
      this.canNext.set(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    this.cleanup = () => el.removeEventListener('scroll', update);
  }

  protected sigColor(color: number | undefined): string {
    return color ? this.colorService.convertToHex(color) : '#1456BE';
  }
}
